"use client";

import { Monitor } from "lucide-react";
import { useTranslations } from "next-intl";
import { memo, useEffect, useState } from "react";

interface ScreenShareWarningProps {
  isScreenSharing: boolean;
  onStopPresenting: () => void;
}

export const ScreenShareWarning = memo(({ isScreenSharing, onStopPresenting }: ScreenShareWarningProps) => {
  const t = useTranslations();
  const [isDismissed, setIsDismissed] = useState(false);
  const stopPresenting = () => {
    onStopPresenting();
    setIsDismissed(true);
  };

  useEffect(() => {
    if (!isScreenSharing) {
      setIsDismissed(false);
    }
  }, [isScreenSharing]);

  if (!isScreenSharing || isDismissed) return null;

  return (
    <div
      className="absolute inset-0 z-[40] flex items-center justify-center rounded-xl bg-black/75 p-4"
      onClick={(event) => event.stopPropagation()}
    >
      <section
        aria-labelledby="screen-share-warning-title"
        aria-describedby="screen-share-warning-description"
        className="flex w-full max-w-lg flex-col items-center gap-4 text-center text-white"
        role="dialog"
      >
        <Monitor className="h-8 w-8 text-white/80" aria-hidden="true" />
        <h2 id="screen-share-warning-title" className="text-xl font-medium">
          {t("meeting.screenShareWarningTitle")}
        </h2>
        <p id="screen-share-warning-description" className="max-w-md text-sm text-white/90">
          {t("meeting.screenShareWarningMessage")}
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <button
            type="button"
            onClick={() => setIsDismissed(true)}
            className="rounded-full border border-white/60 px-5 py-2 text-sm font-medium transition-colors hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            {t("meeting.showScreenAnyway")}
          </button>
          <button
            type="button"
            onClick={stopPresenting}
            className="rounded-full border border-white/60 px-5 py-2 text-sm font-medium transition-colors hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            {t("meeting.stopPresenting")}
          </button>
        </div>
      </section>
    </div>
  );
});

ScreenShareWarning.displayName = "ScreenShareWarning";
